import React from 'react';
import { Shield, Lock, FileText, Users, AlertCircle, Database, Network, Settings, CheckCircle, XCircle, Info } from 'lucide-react';

const SecurityPolicy = () => {
  const currentDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  const StatusBadge = ({ status, text }) => {
    const config = {
      implemented: { icon: CheckCircle, colors: 'bg-green-100 text-green-800 border-green-200' },
      partial: { icon: Info, colors: 'bg-blue-100 text-blue-800 border-blue-200' },
      notApplicable: { icon: XCircle, colors: 'bg-gray-100 text-gray-600 border-gray-200' }
    };
    
    const { icon: Icon, colors } = config[status];

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${colors} whitespace-nowrap flex-shrink-0`}>
        <Icon className="h-3 w-3 mr-1 flex-shrink-0" />
        <span className="truncate">{text}</span>
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex-shrink-0">
              <a href="/" className="text-2xl text-gray-800 font-black tracking-wide">
                ZYKAIN
              </a>
            </div>
          </div>
        </div>
      </nav>

      <div className="bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-xl p-8 mb-8">
          <div className="flex items-center justify-center mb-6">
            <Shield className="h-12 w-12 text-blue-600 mr-4" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Sprint Reporter</h1>
              <h2 className="text-xl font-semibold text-blue-600">Security Policy</h2>
            </div>
          </div>
          <div className="text-center text-gray-600">
            <p>Last Updated: {currentDate}</p>
            <p>Version: 1.0</p>
          </div>
        </div>

        {/* App Architecture Overview */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Network className="h-5 w-5 mr-2 text-blue-600" />
            Application Architecture
          </h3>
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
            <p className="text-blue-800">
              <strong>Forge-Based Application:</strong> Sprint Reporter is built using Atlassian's Forge platform 
              with minimal external dependencies. The app leverages Atlassian's secure infrastructure while 
              integrating with external services for enhanced functionality.
            </p>
          </div>
          <div className="mt-4 space-y-2">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <span>Core Framework: Atlassian Forge</span>
              <StatusBadge status="implemented" text="Implemented" />
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <span>External Integrations: Slack (Optional)</span>
              <StatusBadge status="partial" text="Optional Feature" />
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <span>Data Storage: Atlassian + Forge Storage</span>
              <StatusBadge status="implemented" text="Secure" />
            </div>
          </div>
        </div>

        {/* Content Sections */}
        <div className="space-y-8">
          
          {/* Security Overview */}
          <section className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Shield className="h-6 w-6 mr-2 text-blue-600" />
              1. Security Overview
            </h3>
            <div className="prose prose-blue max-w-none">
              <p className="text-gray-700 mb-4">
                Sprint Reporter App is committed to maintaining security standards appropriate for our 
                Forge-based architecture. We leverage Atlassian's secure infrastructure while implementing 
                additional security measures for external integrations.
              </p>
              
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Key Security Principles</h4>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Leverage Atlassian Forge's built-in security controls</li>
                <li>Minimal external data exposure</li>
                <li>Optional integrations with clear user consent</li>
                <li>No collection of user credentials or API tokens</li>
                <li>Secure handling of configuration data</li>
              </ul>
            </div>
          </section>

          {/* Authentication & Authorization */}
          <section className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Lock className="h-6 w-6 mr-2 text-green-600" />
              2. Authentication & Authorization
            </h3>
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-green-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">Forge Framework Authentication</span>
                  <p className="text-sm text-gray-600">All authentication handled by Atlassian's Forge platform</p>
                </div>
                <StatusBadge status="implemented" text="Built-in" />
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-green-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">No API Token Collection</span>
                  <p className="text-sm text-gray-600">We do not collect, store, or manage user API tokens</p>
                </div>
                <StatusBadge status="implemented" text="Compliant" />
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-green-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">License Validation</span>
                  <p className="text-sm text-gray-600">Basic license checking implemented in resolver functions</p>
                </div>
                <StatusBadge status="implemented" text="Basic Implementation" />
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h5 className="font-medium text-gray-900 mb-2">Authentication Details:</h5>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• User authentication managed entirely by Atlassian</li>
                  <li>• App permissions granted through Atlassian's standard OAuth flow</li>
                  <li>• No custom authentication or session management</li>
                  <li>• License validation prevents unauthorized access to paid features</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Data Protection */}
          <section className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Database className="h-6 w-6 mr-2 text-purple-600" />
              3. Data Protection & Privacy
            </h3>
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-purple-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">Data Minimization</span>
                  <p className="text-sm text-gray-600">Only collect configuration data necessary for app functionality</p>
                </div>
                <StatusBadge status="implemented" text="Implemented" />
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-purple-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">Forge Storage Security</span>
                  <p className="text-sm text-gray-600">Configuration stored using Forge's encrypted storage</p>
                </div>
                <StatusBadge status="implemented" text="Atlassian Managed" />
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-blue-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">Optional Slack Integration</span>
                  <p className="text-sm text-gray-600">Webhook URLs stored securely, notifications sent via HTTPS</p>
                </div>
                <StatusBadge status="partial" text="User Configured" />
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h5 className="font-medium text-gray-900 mb-2">Data Handling Practices:</h5>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• <strong>Configuration Data:</strong> Board IDs, issue types, custom field mappings stored in Forge storage</li>
                  <li>• <strong>No User Data Storage:</strong> We don't store issue content, user profiles, or sensitive data</li>
                  <li>• <strong>Slack Webhooks:</strong> Optional integration, webhook URLs encrypted in storage</li>
                  <li>• <strong>Data Access:</strong> Read-only access to Jira issues for report generation</li>
                  <li>• <strong>Data Retention:</strong> Configuration data retained until app uninstallation</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Infrastructure Security */}
          <section className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Network className="h-6 w-6 mr-2 text-red-600" />
              4. Infrastructure Security
            </h3>
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-red-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">Atlassian Forge Infrastructure</span>
                  <p className="text-sm text-gray-600">Hosted on Atlassian's secure cloud infrastructure</p>
                </div>
                <StatusBadge status="implemented" text="Atlassian Managed" />
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-red-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">HTTPS Communication</span>
                  <p className="text-sm text-gray-600">All external API calls use HTTPS (Slack webhooks)</p>
                </div>
                <StatusBadge status="implemented" text="Enforced" />
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-red-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">Network Isolation</span>
                  <p className="text-sm text-gray-600">Forge functions run in isolated environments</p>
                </div>
                <StatusBadge status="implemented" text="Platform Feature" />
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h5 className="font-medium text-gray-900 mb-2">Infrastructure Details:</h5>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• <strong>Hosting:</strong> Atlassian Forge serverless platform</li>
                  <li>• <strong>External Calls:</strong> Only to optional Slack webhook URLs (HTTPS)</li>
                  <li>• <strong>Domain Security:</strong> No custom domains or subdomains managed</li>
                  <li>• <strong>Function Security:</strong> Isolated execution environment per Forge standards</li>
                </ul>
              </div>
            </div>
          </section>

          {/* External Integrations */}
          <section className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Settings className="h-6 w-6 mr-2 text-orange-600" />
              5. External Integrations & Risk Management
            </h3>
            <div className="space-y-4">
              <div className="bg-orange-50 border-l-4 border-orange-400 p-4">
                <h5 className="font-medium text-orange-800 mb-2">Slack Integration (Optional)</h5>
                <p className="text-orange-700 text-sm">
                  Users can optionally configure Slack webhook notifications. This is entirely user-controlled 
                  and webhook URLs are validated before use.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h6 className="font-medium text-gray-900 mb-2">Security Measures:</h6>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Webhook URL validation</li>
                    <li>• HTTPS-only communication</li>
                    <li>• Error handling and logging</li>
                    <li>• Test functionality for users</li>
                  </ul>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h6 className="font-medium text-gray-900 mb-2">Data Sent to Slack:</h6>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Sprint name and completion metrics</li>
                    <li>• Project name</li>
                    <li>• Link to Confluence report</li>
                    <li>• No sensitive user data</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Vulnerability Management */}
          <section className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <AlertCircle className="h-6 w-6 mr-2 text-yellow-600" />
              6. Vulnerability Management
            </h3>
            <div className="space-y-4">
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h5 className="font-medium text-yellow-800 mb-2">Current Security Measures:</h5>
                <ul className="text-yellow-700 text-sm space-y-1">
                  <li>• Regular dependency updates as part of development process</li>
                  <li>• Forge platform security managed by Atlassian</li>
                  <li>• Input validation for user-provided configuration</li>
                  <li>• Error handling to prevent information disclosure</li>
                </ul>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h5 className="font-medium text-blue-800 mb-2">Planned Improvements:</h5>
                <ul className="text-blue-700 text-sm space-y-1">
                  <li>• Automated dependency scanning</li>
                  <li>• Enhanced logging and monitoring</li>
                  <li>• Formal incident response procedures</li>
                  <li>• Regular security assessment schedule</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Compliance & Standards */}
          <section className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <FileText className="h-6 w-6 mr-2 text-indigo-600" />
              7. Compliance & Standards
            </h3>
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-indigo-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">Atlassian Marketplace Requirements</span>
                  <p className="text-sm text-gray-600">Designed to meet mandatory security requirements</p>
                </div>
                <StatusBadge status="implemented" text="Compliant" />
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-indigo-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">API Token Policy</span>
                  <p className="text-sm text-gray-600">No collection of user API tokens</p>
                </div>
                <StatusBadge status="implemented" text="Policy Compliant" />
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-blue-50 rounded-lg">
                <div className="flex-1">
                  <span className="font-medium">Data Privacy</span>
                  <p className="text-sm text-gray-600">Minimal data collection with user consent</p>
                </div>
                <StatusBadge status="partial" text="Basic Implementation" />
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h5 className="font-medium text-gray-900 mb-2">Compliance Status:</h5>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• <strong>Forge Security Requirements:</strong> Leverages platform security controls</li>
                  <li>• <strong>HTTPS Communication:</strong> All external communications use HTTPS</li>
                  <li>• <strong>No Credential Storage:</strong> Compliant with API token collection prohibition</li>
                  <li>• <strong>User Data Protection:</strong> Minimal collection, secure storage</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Security Contact */}
          <section className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Users className="h-6 w-6 mr-2 text-blue-600" />
              8. Security Contact Information
            </h3>
            <div className="prose prose-blue max-w-none">
              <p className="text-gray-700 mb-4">
                For security-related inquiries, vulnerability reports, or questions about our security practices, 
                please contact us using the following information:
              </p>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">Security Contact</h4>
                <ul className="space-y-2 text-gray-700">
                  <li><strong>Email:</strong> hello@zykain.com</li>
                  <li><strong>Response Time:</strong> Within 48 hours for security issues</li>
                  <li><strong>Scope:</strong> Security vulnerabilities, privacy concerns, compliance questions</li>
                </ul>
              </div>

              <h4 className="text-lg font-semibold text-gray-900 mt-6 mb-2">Responsible Disclosure</h4>
              <p className="text-gray-700 mb-4">
                We appreciate responsible disclosure of security vulnerabilities. If you discover a security 
                issue with our app:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Report the issue to our security contact email</li>
                <li>Provide detailed information about the vulnerability</li>
                <li>Allow reasonable time for investigation and remediation</li>
                <li>Avoid accessing or modifying user data during testing</li>
              </ul>
            </div>
          </section>

        </div>

        {/* Final Note (before footer) */}
        <div className="bg-white p-6 mt-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center">
              <p className="text-gray-600 text-sm mb-2">
                This security policy reflects the current implementation of Sprint Reporter App and our 
                commitment to security within the constraints of our Forge-based architecture.
              </p>
              <p className="text-gray-500 text-xs">
                © 2025 Sprint Reporter App. All rights reserved.
              </p>
            </div>
            
            <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-yellow-800 text-sm">
                <strong>Note:</strong> This app leverages Atlassian's Forge platform for core security 
                controls. Many enterprise-grade security features are inherited from Atlassian's 
                infrastructure rather than implemented directly in the app code.
              </p>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="text-white py-12" style={{ backgroundColor: '#16163F' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h3 className="text-2xl font-black tracking-wide mb-4">ZYKAIN</h3>
            <p className="text-gray-400 mb-6">Smart Apps, Smarter Solutions</p>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-3">
              <a
                href="/privacy-policy"
                className="text-gray-400 hover:text-white transition-colors"
              >
                Privacy Policy
              </a>
              <a
                href="/sla-policy"
                className="text-gray-400 hover:text-white transition-colors"
              >
                Service Level Policy
              </a>
              <a 
                href="/terms-of-service" 
                className="text-gray-400 hover:text-white transition-colors"
              >
                Terms of Service
              </a>
              <a 
                href="/sprint-reporter-faq" 
                className="text-gray-400 hover:text-white transition-colors"
              >
                FAQs
              </a>
              <a 
                href="/support" 
                className="text-gray-400 hover:text-white transition-colors"
              >
                Support
              </a>
              <a
                href="/#contact"
                className="text-gray-400 hover:text-white transition-colors"
              >
                Contact
              </a>
            </div>
            <div className="mt-8 pt-8 border-t border-gray-800">
              <p className="text-gray-500 text-sm">
                © 2025 ZYKAIN. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SecurityPolicy;